<template>
  <JoszakiCard>
    <div class="grid grid-cols-[auto_1fr] gap-2">
      <UserAvatar
        :image="
          $helpers.getProfilePictureThumbnailUrl(answer.person.profilePhotoId)
        "
      />
      <div>
        <div class="flex justify-between">
          <div class="flex flex-col md:flex-row md:gap-1">
            <NuxtLink :to="professionalLink">
              {{ answer.person.name }}
            </NuxtLink>
            <div
              class="flex items-center gap-1 text-xs max-md:hidden [&>*]:before:content-['·']"
            >
              <RatingIndicator
                v-if="answer.person.score > 0"
                :score="answer.person.score"
                class="inline-flex"
              />
              <span v-if="answer.person.winCount > 0">
                {{
                  $t("personCard.comission", { count: answer.person.winCount })
                }}
                <IconComponent icon="check" class="text-success inline" />
              </span>
              <span v-if="answer.person.answerCount > 0">
                {{
                  $t("personCard.answers", { count: answer.person.answerCount })
                }}
                <IconComponent
                  icon="comments"
                  class="text-pink-400 inline text-xs"
                />
              </span>
            </div>
          </div>

          <div class="flex flex-row items-center gap-5">
            <FavouriteButton
              :entity-type="FAVOURITE_ENTITY_TYPES.ANSWER"
              :entity-id="answer.id"
              class="h-5 w-5"
            />
            <IconComponent
              v-if="canEditAnswer"
              icon="trash"
              class="cursor-pointer hover:text-primary"
              @click.native="openModal(MODAL_DELETE_ANSWER)"
            />
            <IconComponent
              v-if="canEditAnswer"
              icon="pencil-alt"
              class="cursor-pointer hover:text-primary"
              @click.native="openModal(MODAL_EDIT_ANSWER)"
            />
            <IconComponent
              v-if="me?.isAdmin"
              icon="exclamation-circle"
              class="cursor-pointer hover:text-primary"
              @click.native="openReportModal"
            />
          </div>
        </div>
        <div
          class="flex items-center gap-1 text-xs md:hidden mb-2 [&>*:not(:first-child)]:before:content-['·']"
        >
          <RatingIndicator
            v-if="answer.person.score > 0"
            :score="answer.person.score"
            class="inline-flex"
          />
          <span v-if="answer.person.winCount > 0">
            {{ $t("personCard.comission", { count: answer.person.winCount }) }}
            <IconComponent icon="check" class="text-success inline" />
          </span>
          <span v-if="answer.person.answerCount > 0">
            {{ $t("personCard.answers", { count: answer.person.answerCount }) }}
            <IconComponent
              icon="comments"
              class="text-pink-400 inline text-xs"
            />
          </span>
        </div>
        <p class="whitespace-pre-line">
          {{ answer.body }}
        </p>
        <template v-if="answer.linkedAnswer">
          <NuxtLink :to="linkedAnswerQuestionLink">
            {{
              $t("questionPage.answerCard.linkedAnswerQuestion", {
                questionName: answer.linkedAnswer.question?.title,
              })
            }}
          </NuxtLink>
          <JoszakiCard class="grid grid-cols-[auto_1fr] gap-5">
            <UserAvatar
              :image="
                $helpers.getProfilePictureThumbnailUrl(
                  answer.person.profilePhotoId
                )
              "
            />
            <div>
              <NuxtLink :to="professionalLink">
                {{ answer.person.name }}
              </NuxtLink>
              <p v-if="answer.linkedAnswer.deleted" class="text-error">
                {{ $t("questionPage.answerCard.linkedAnswerIsDeleted") }}
              </p>
              <p v-else class="whitespace-pre-line">
                {{ answer.linkedAnswer.body }}
              </p>
              <div class="flex flex-row justify-between mt-4 items-center">
                <span class="text-xs text-gray-400">
                  {{ formatDate(answer.linkedAnswer.created) }}
                  <template v-if="answer.linkedAnswer.updated">
                    {{
                      $t("questionPage.editedAt", {
                        date: formatDate(answer.linkedAnswer.updated),
                      })
                    }}
                  </template>
                </span>
              </div>
            </div>
          </JoszakiCard>
        </template>
        <div class="flex flex-row justify-between mt-4 items-center">
          <div class="flex gap-2">
            <JoszakiButton
              size="sm"
              class="text-xs"
              icon-left="thumbs-up"
              :loading="isVoting"
              @click="upvoteClicked"
            >
              {{ answer.upvotes }}
            </JoszakiButton>
            <!-- Disable downvote for now
              <JoszakiButton
              size="sm"
              type="error"
              class="text-xs"
              icon-left="thumbs-down"
              :loading="isVoting"
              @click="vote(false, answer)"
            >
              {{ answer.downvotes }}
            </JoszakiButton>  -->
          </div>
          <span class="text-xs text-gray-400">
            {{ formatDate(answer.created) }}
            <template v-if="answer.updated">
              {{
                $t("questionPage.editedAt", {
                  date: formatDate(answer.updated),
                })
              }}
            </template>
          </span>
        </div>
      </div>
    </div>
    <div class="flex flex-row mt-4 gap-2">
      <JoszakiButton outline inverted class="flex-1" @click="openProfessional">
        <span class="max-md:hidden">{{
          $t("questionPage.answerCard.goToProfessional")
        }}</span>
        <span class="md:hidden">{{
          $t("questionPage.answerCard.goToProfessionalSmall")
        }}</span>
      </JoszakiButton>
      <JoszakiButton
        class="flex-1"
        @click="
          onRequestTender({ source: 'answer-card', person: answer.person })
        "
      >
        {{ $t("questionPage.answerCard.requestTender") }}
      </JoszakiButton>
    </div>
  </JoszakiCard>
</template>

<script>
import { mapState } from "pinia";
import UserAvatar from "~/components/UserAvatar.vue";
import { useUserStore } from "~/stores/user";
import {
  MODAL_DELETE_ANSWER,
  MODAL_EDIT_ANSWER,
  MODAL_REPORT_ANSWER,
  useQuestionStore,
} from "~/stores/question/question";
import { differenceToNow } from "~/helpers/date";
import RatingIndicator from "~/components/_refactored/rating/RatingIndicator.vue";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";
import { useRequestTender } from "~/composables/requestTender";
import { useAnswerVoter } from "~/composables/question/useUpvoteAnswer";

export default {
  components: { RatingIndicator, UserAvatar, FavouriteButton },
  props: {
    answer: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const questionStore = useQuestionStore();
    const { upvoteAnswer, loading } = useAnswerVoter();

    async function upvoteClicked() {
      const { answer } = await upvoteAnswer(props.answer);
      if (answer) {
        questionStore.patchAnswer(answer);
      }
    }

    return {
      MODAL_DELETE_ANSWER,
      MODAL_EDIT_ANSWER,
      FAVOURITE_ENTITY_TYPES,
      questionStore,
      upvoteClicked,
      isVoting: loading,
      ...useRequestTender(),
    };
  },
  computed: {
    ...mapState(useUserStore, {
      me: "me",
    }),
    canEditAnswer() {
      if (!this.me) {
        return false;
      }

      return this.me.isAdmin || this.answer?.person?.id === this.me.id;
    },
    professionalLink() {
      return this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: this.answer.person.seoName,
        },
      });
    },
    linkedAnswerQuestionLink() {
      return this.localePath({
        name: "questionPage",
        params: {
          questionSeoName: this.answer?.linkedAnswer?.question?.seoName ?? "",
        },
      });
    },
  },
  methods: {
    openModal(modalId) {
      this.questionStore.openModal(modalId, {
        answer: this.answer,
      });
    },
    formatDate(date) {
      return differenceToNow(this, date);
    },
    onRequestTender({ source, person }) {
      this.requestTender({
        source,
        person,
      });
    },
    openReportModal() {
      if (!this.me) {
        this.$joszaki.toast({
          message: this.$t("questionPage.answerCard.onlyLoggedInCanReport"),
          position: "is-top",
          type: "info",
        });
        return;
      }

      this.openModal(MODAL_REPORT_ANSWER);
    },
    /**
     * @deprecated
     * TODO: if downvote becomes obsolete this should be removed
     */
    async vote(isUp, answer, linkedAnswer) {
      if (!answer && !linkedAnswer) {
        return;
      }

      if (!this.me) {
        this.$joszaki.toast({
          message: this.$t("questionPage.answerCard.onlyLoggedInCanVote"),
          position: "is-top",
          type: "info",
        });
        return;
      }

      try {
        if (linkedAnswer) {
          this.isVotingOnLinkedAnswer = true;
        } else {
          this.isVoting = true;
        }

        const answerId = Number(linkedAnswer?.id ?? answer.id);
        if (isUp) {
          await this.questionStore.upvoteAnswer(answerId);
        } else {
          await this.questionStore.downvoteAnswer(answerId);
        }

        this.$joszaki.toast({
          message: this.$t("questionPage.answerCard.successVote"),
          position: "is-top",
          type: "success",
        });

        this.questionStore.reloadQuestion();
      } catch (e) {
        this.$joszaki.toast({
          message: this.$t("questionPage.answerCard.errorVote"),
          position: "is-top",
          type: "danger",
        });
      } finally {
        if (linkedAnswer) {
          this.isVotingOnLinkedAnswer = false;
        } else {
          this.isVoting = false;
        }
      }
    },
    openProfessional() {
      window.open(this.professionalLink, "_blank");
    },
  },
};
</script>
